import React, { CSSProperties, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { gql, useMutation, useQuery } from '@apollo/client';
import { AlertCircle, ArrowLeft, Check, Loader2, Plus, Save, Trash2 } from 'lucide-react';
import { useDownloadAttachment, useLoadHttpClientEmpty } from '../../../App.hooks';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

type MappedItem = { value: string; mapped: string };
type ColourItem = { r: number; g: number; b: number; mapped: string };
type BoolMapped = { trueToken: string; falseToken: string };
type BackgroundMapped = { opaqueToken: string; transparentToken: string };
type BackgroundValue = 'Opaque' | 'Transparent';

type FixedType = {
    name: string;
    font: string;
    textSize: string;
    colour: { r: number; g: number; b: number } | null;
    underline: boolean;
    bold: boolean;
    arrowHead: string;
    weight: string;
    widthFactor: string;
    border: boolean;
    background: BackgroundValue;
};

type TextStyleData = {
    Font: { value: string; mapped: string };
    TextSizes: MappedItem[];
    TabSizes: MappedItem[];
    Colours: ColourItem[];
    ColourTolerance: number;
    Underline: BoolMapped;
    Bold: BoolMapped;
    ArrowHeads: MappedItem[];
    Weights: MappedItem[];
    WidthFactors: MappedItem[];
    Border: BoolMapped;
    Background: BackgroundMapped;
    NomenclaturePattern: string;
    FixedTypes: FixedType[];
};

const defaultData = (): TextStyleData => ({
    Font: { value: '', mapped: '' },
    TextSizes: [],
    TabSizes: [],
    Colours: [],
    ColourTolerance: 30,
    Underline: { trueToken: 'U', falseToken: '' },
    Bold: { trueToken: 'B', falseToken: '' },
    ArrowHeads: [],
    Weights: [],
    WidthFactors: [],
    Border: { trueToken: 'BD', falseToken: '' },
    Background: { opaqueToken: 'OP', transparentToken: 'TR' },
    NomenclaturePattern: '',
    FixedTypes: [],
});

const DYNAMIC_DATA_TYPE = 'Standards';
const DYNAMIC_DATA_NAME = 'Text Style';

const COLOUR_TOLERANCE_OPTIONS = [
    {
        value: 15,
        label: 'High Precision',
        description: 'Exact brand guidelines, strict compliance audits.',
    },
    {
        value: 30,
        label: 'Balanced (Default)',
        description: 'General matching, adjusting for minor screen/encoder variations.',
    },
    {
        value: 50,
        label: 'Permissive',
        description: 'Rough grouping or matching heavily degraded/compressed assets.',
    },
] as const;

const NOMENCLATURE_TOKENS = [
    'Font',
    'TextSize',
    'TabSize',
    'Colour',
    'Underline',
    'Bold',
    'ArrowHead',
    'Weight',
    'WidthFactor',
    'Border',
    'Background',
] as const;

/* ------------------------------------------------------------------ */
/*  GraphQL                                                            */
/* ------------------------------------------------------------------ */

const GET_PROJECT_TEXT_STYLE_DATA = gql`
    query getProjectTextStyleData($id: String) {
        Project(id: $id) {
            dynamicData { id name type data setData }
        }
    }
`;

const SET_PROJECT_DYNAMIC_DATA = gql`
    mutation setTextStyleDynamicData($projectId: String, $data: DynamicDataInput) {
        Project(id: $projectId) { setDynamicData(data: $data) }
    }
`;

/* ------------------------------------------------------------------ */
/*  Colour utilities                                                   */
/* ------------------------------------------------------------------ */

function rgbToHex(r: number, g: number, b: number): string {
    return (
        '#' +
        [r, g, b]
            .map(v => Math.max(0, Math.min(255, v)).toString(16).padStart(2, '0'))
            .join('')
    );
}

function hexToRgb(hex: string): { r: number; g: number; b: number } {
    const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return m
        ? { r: parseInt(m[1], 16), g: parseInt(m[2], 16), b: parseInt(m[3], 16) }
        : { r: 0, g: 0, b: 0 };
}

/* ------------------------------------------------------------------ */
/*  SectionCard                                                        */
/* ------------------------------------------------------------------ */

const SectionCard = ({
    title,
    description,
    children,
}: {
    title: string;
    description?: string;
    children: React.ReactNode;
}) => (
    <div
        className="card p-5"
        style={{
            border: '1px solid var(--border-default)',
            borderRadius: 10,
            marginBottom: 16,
        }}
    >
        <div className="mb-3">
            <p
                className="text-sm font-semibold"
                style={{ color: 'var(--text-primary)', marginBottom: description ? 2 : 0 }}
            >
                {title}
            </p>
            {description && (
                <p className="text-xs" style={{ color: 'var(--text-tertiary)' }}>
                    {description}
                </p>
            )}
        </div>
        {children}
    </div>
);

/* ------------------------------------------------------------------ */
/*  MappedListEditor — for string-based list properties               */
/* ------------------------------------------------------------------ */

type MappedListEditorProps = {
    items: MappedItem[];
    onChange: (items: MappedItem[]) => void;
    placeholder?: string;
    addLabel?: string;
    valueType?: 'text' | 'number';
    step?: number;
};

const MappedListEditor = ({
    items,
    onChange,
    placeholder = 'Enter value…',
    addLabel = 'Add',
    valueType = 'text',
    step,
}: MappedListEditorProps) => {
    const [newValue, setNewValue] = useState('');
    const [newMapped, setNewMapped] = useState('');

    const handleAdd = () => {
        const trimmed = newValue.trim();
        if (!trimmed) return;
        if (valueType === 'number' && !Number.isFinite(Number(trimmed))) return;
        onChange([...items, { value: trimmed, mapped: newMapped.trim() }]);
        setNewValue('');
        setNewMapped('');
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') handleAdd();
    };

    return (
        <div>
            {items.length > 0 && (
                <table
                    style={{
                        width: '100%',
                        borderCollapse: 'collapse',
                        marginBottom: 10,
                        fontSize: 13,
                    }}
                >
                    <thead>
                        <tr>
                            <th
                                style={{
                                    textAlign: 'left',
                                    padding: '4px 8px 6px',
                                    color: 'var(--text-tertiary)',
                                    fontWeight: 600,
                                    fontSize: 11,
                                    width: '50%',
                                }}
                            >
                                Value
                            </th>
                            <th
                                style={{
                                    textAlign: 'left',
                                    padding: '4px 8px 6px',
                                    color: 'var(--text-tertiary)',
                                    fontWeight: 600,
                                    fontSize: 11,
                                    width: '44%',
                                }}
                            >
                                Mapped Token
                            </th>
                            <th style={{ width: 32 }} />
                        </tr>
                    </thead>
                    <tbody>
                        {items.map((item, i) => (
                            <tr
                                key={i}
                                style={{
                                    borderTop: '1px solid var(--border-subtle, var(--border-default))',
                                }}
                            >
                                <td style={{ padding: '4px 8px' }}>
                                    <input
                                        className="input-base w-full"
                                        type={valueType}
                                        step={step}
                                        style={{ fontSize: 12, padding: '3px 7px' }}
                                        value={item.value}
                                        onChange={e => {
                                            const next = [...items];
                                            next[i] = { ...next[i], value: e.target.value };
                                            onChange(next);
                                        }}
                                    />
                                </td>
                                <td style={{ padding: '4px 8px' }}>
                                    <input
                                        className="input-base w-full"
                                        style={{
                                            fontSize: 12,
                                            padding: '3px 7px',
                                            fontFamily: 'monospace',
                                        }}
                                        value={item.mapped}
                                        placeholder="token"
                                        onChange={e => {
                                            const next = [...items];
                                            next[i] = { ...next[i], mapped: e.target.value };
                                            onChange(next);
                                        }}
                                    />
                                </td>
                                <td style={{ padding: '4px 4px 4px 0' }}>
                                    <button
                                        onClick={() =>
                                            onChange(items.filter((_, j) => j !== i))
                                        }
                                        style={{
                                            background: 'none',
                                            border: 'none',
                                            cursor: 'pointer',
                                            color: 'var(--text-tertiary)',
                                            padding: 4,
                                            display: 'flex',
                                            alignItems: 'center',
                                        }}
                                        title="Remove"
                                    >
                                        <Trash2 size={13} />
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}
            <div className="flex items-center gap-2">
                <input
                    className="input-base flex-1"
                    type={valueType}
                    step={step}
                    style={{ fontSize: 12 }}
                    placeholder={placeholder}
                    value={newValue}
                    onChange={e => setNewValue(e.target.value)}
                    onKeyDown={handleKeyDown}
                />
                <input
                    className="input-base"
                    style={{ fontSize: 12, width: 120, fontFamily: 'monospace' }}
                    placeholder="mapped token"
                    value={newMapped}
                    onChange={e => setNewMapped(e.target.value)}
                    onKeyDown={handleKeyDown}
                />
                <button
                    className="btn-secondary text-xs flex items-center gap-1"
                    style={{ flexShrink: 0 }}
                    onClick={handleAdd}
                    disabled={!newValue.trim()}
                >
                    <Plus size={12} /> {addLabel}
                </button>
            </div>
        </div>
    );
};

/* ------------------------------------------------------------------ */
/*  ColourListEditor                                                   */
/* ------------------------------------------------------------------ */

type ColourListEditorProps = {
    items: ColourItem[];
    onChange: (items: ColourItem[]) => void;
};

const ColourListEditor = ({ items, onChange }: ColourListEditorProps) => {
    const [newHex, setNewHex] = useState('#000000');
    const [newMapped, setNewMapped] = useState('');

    const handleAdd = () => {
        const { r, g, b } = hexToRgb(newHex);
        onChange([...items, { r, g, b, mapped: newMapped.trim() }]);
        setNewHex('#000000');
        setNewMapped('');
    };

    const newRgb = hexToRgb(newHex);

    return (
        <div>
            {items.length > 0 && (
                <table
                    style={{
                        width: '100%',
                        borderCollapse: 'collapse',
                        marginBottom: 10,
                        fontSize: 13,
                    }}
                >
                    <thead>
                        <tr>
                            <th
                                style={{
                                    textAlign: 'left',
                                    padding: '4px 8px 6px',
                                    color: 'var(--text-tertiary)',
                                    fontWeight: 600,
                                    fontSize: 11,
                                    width: 44,
                                }}
                            >
                                Colour
                            </th>
                            <th
                                style={{
                                    textAlign: 'left',
                                    padding: '4px 8px 6px',
                                    color: 'var(--text-tertiary)',
                                    fontWeight: 600,
                                    fontSize: 11,
                                }}
                            >
                                RGB
                            </th>
                            <th
                                style={{
                                    textAlign: 'left',
                                    padding: '4px 8px 6px',
                                    color: 'var(--text-tertiary)',
                                    fontWeight: 600,
                                    fontSize: 11,
                                    width: '35%',
                                }}
                            >
                                Mapped Token
                            </th>
                            <th style={{ width: 32 }} />
                        </tr>
                    </thead>
                    <tbody>
                        {items.map((item, i) => {
                            const hex = rgbToHex(item.r, item.g, item.b);
                            return (
                                <tr
                                    key={i}
                                    style={{
                                        borderTop:
                                            '1px solid var(--border-subtle, var(--border-default))',
                                    }}
                                >
                                    <td style={{ padding: '4px 8px' }}>
                                        <input
                                            type="color"
                                            value={hex}
                                            onChange={e => {
                                                const { r, g, b } = hexToRgb(e.target.value);
                                                const next = [...items];
                                                next[i] = { ...next[i], r, g, b };
                                                onChange(next);
                                            }}
                                            style={{
                                                width: 32,
                                                height: 28,
                                                padding: 2,
                                                border: '1px solid var(--border-default)',
                                                borderRadius: 4,
                                                cursor: 'pointer',
                                            }}
                                        />
                                    </td>
                                    <td
                                        style={{
                                            padding: '4px 8px',
                                            color: 'var(--text-secondary)',
                                            fontSize: 12,
                                            fontFamily: 'monospace',
                                        }}
                                    >
                                        {`rgb(${item.r}, ${item.g}, ${item.b})`}
                                    </td>
                                    <td style={{ padding: '4px 8px' }}>
                                        <input
                                            className="input-base w-full"
                                            style={{
                                                fontSize: 12,
                                                padding: '3px 7px',
                                                fontFamily: 'monospace',
                                            }}
                                            value={item.mapped}
                                            placeholder="token"
                                            onChange={e => {
                                                const next = [...items];
                                                next[i] = { ...next[i], mapped: e.target.value };
                                                onChange(next);
                                            }}
                                        />
                                    </td>
                                    <td style={{ padding: '4px 4px 4px 0' }}>
                                        <button
                                            onClick={() =>
                                                onChange(items.filter((_, j) => j !== i))
                                            }
                                            style={{
                                                background: 'none',
                                                border: 'none',
                                                cursor: 'pointer',
                                                color: 'var(--text-tertiary)',
                                                padding: 4,
                                                display: 'flex',
                                                alignItems: 'center',
                                            }}
                                            title="Remove"
                                        >
                                            <Trash2 size={13} />
                                        </button>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            )}
            <div className="flex items-center gap-2">
                <input
                    type="color"
                    value={newHex}
                    onChange={e => setNewHex(e.target.value)}
                    style={{
                        width: 36,
                        height: 32,
                        padding: 2,
                        border: '1px solid var(--border-default)',
                        borderRadius: 4,
                        cursor: 'pointer',
                        flexShrink: 0,
                    }}
                />
                <span
                    className="text-xs"
                    style={{
                        color: 'var(--text-tertiary)',
                        fontFamily: 'monospace',
                        minWidth: 130,
                    }}
                >
                    {`rgb(${newRgb.r}, ${newRgb.g}, ${newRgb.b})`}
                </span>
                <input
                    className="input-base flex-1"
                    style={{ fontSize: 12, fontFamily: 'monospace' }}
                    placeholder="mapped token"
                    value={newMapped}
                    onChange={e => setNewMapped(e.target.value)}
                    onKeyDown={e => {
                        if (e.key === 'Enter') handleAdd();
                    }}
                />
                <button
                    className="btn-secondary text-xs flex items-center gap-1"
                    style={{ flexShrink: 0 }}
                    onClick={handleAdd}
                >
                    <Plus size={12} /> Add Colour
                </button>
            </div>
        </div>
    );
};

/* ------------------------------------------------------------------ */
/*  BoolMappedEditor                                                   */
/* ------------------------------------------------------------------ */

type BoolMappedEditorProps = {
    label: string;
    value: BoolMapped;
    onChange: (value: BoolMapped) => void;
};

const BoolMappedEditor = ({ label, value, onChange }: BoolMappedEditorProps) => (
    <div className="flex items-center gap-4 flex-wrap">
        <span
            className="text-sm font-medium"
            style={{ color: 'var(--text-primary)', minWidth: 72 }}
        >
            {label}
        </span>
        <div className="flex items-center gap-2">
            <span className="text-xs" style={{ color: 'var(--text-tertiary)', minWidth: 44 }}>
                True →
            </span>
            <input
                className="input-base"
                style={{ fontSize: 12, width: 110, fontFamily: 'monospace' }}
                value={value.trueToken}
                placeholder="e.g. U"
                onChange={e => onChange({ ...value, trueToken: e.target.value })}
            />
        </div>
        <div className="flex items-center gap-2">
            <span className="text-xs" style={{ color: 'var(--text-tertiary)', minWidth: 44 }}>
                False →
            </span>
            <input
                className="input-base"
                style={{ fontSize: 12, width: 110, fontFamily: 'monospace' }}
                value={value.falseToken}
                placeholder="(blank to omit)"
                onChange={e => onChange({ ...value, falseToken: e.target.value })}
            />
        </div>
    </div>
);

/* ------------------------------------------------------------------ */
/*  BackgroundMappedEditor — two fixed options (Opaque / Transparent)  */
/* ------------------------------------------------------------------ */

type BackgroundMappedEditorProps = {
    label: string;
    value: BackgroundMapped;
    onChange: (value: BackgroundMapped) => void;
};

const BackgroundMappedEditor = ({ label, value, onChange }: BackgroundMappedEditorProps) => (
    <div className="flex items-center gap-4 flex-wrap">
        <span
            className="text-sm font-medium"
            style={{ color: 'var(--text-primary)', minWidth: 72 }}
        >
            {label}
        </span>
        <div className="flex items-center gap-2">
            <span className="text-xs" style={{ color: 'var(--text-tertiary)', minWidth: 72 }}>
                Opaque →
            </span>
            <input
                className="input-base"
                style={{ fontSize: 12, width: 110, fontFamily: 'monospace' }}
                value={value.opaqueToken}
                placeholder="e.g. OP"
                onChange={e => onChange({ ...value, opaqueToken: e.target.value })}
            />
        </div>
        <div className="flex items-center gap-2">
            <span className="text-xs" style={{ color: 'var(--text-tertiary)', minWidth: 72 }}>
                Transparent →
            </span>
            <input
                className="input-base"
                style={{ fontSize: 12, width: 110, fontFamily: 'monospace' }}
                value={value.transparentToken}
                placeholder="e.g. TR"
                onChange={e => onChange({ ...value, transparentToken: e.target.value })}
            />
        </div>
    </div>
);

/* ------------------------------------------------------------------ */
/*  FixedTypesEditor                                                   */
/* ------------------------------------------------------------------ */

const emptyFixedType = (): FixedType => ({
    name: '',
    font: '',
    textSize: '',
    colour: null,
    underline: false,
    bold: false,
    arrowHead: '',
    weight: '',
    widthFactor: '',
    border: false,
    background: 'Opaque',
});

type FixedTypesEditorProps = {
    items: FixedType[];
    onChange: (items: FixedType[]) => void;
};

const FixedTypesEditor = ({ items, onChange }: FixedTypesEditorProps) => {
    const patch = (i: number, p: Partial<FixedType>) => {
        const next = [...items];
        next[i] = { ...next[i], ...p };
        onChange(next);
    };

    const thStyle: React.CSSProperties = {
        textAlign: 'left',
        padding: '6px 8px',
        color: 'var(--text-tertiary)',
        fontWeight: 600,
        fontSize: 11,
        whiteSpace: 'nowrap',
        borderBottom: '1px solid var(--border-default)',
        backgroundColor: 'var(--surface-1, var(--surface-100))',
    };

    const tdStyle: React.CSSProperties = {
        padding: '4px 6px',
        verticalAlign: 'middle',
        borderBottom: '1px solid var(--border-subtle, var(--border-default))',
    };

    return (
        <div>
            <div
                className="overflow-auto rounded-lg"
                style={{ border: '1px solid var(--border-default)' }}
            >
                <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 740 }}>
                    <thead>
                        <tr>
                            <th style={{ ...thStyle, minWidth: 130 }}>Name</th>
                            <th style={{ ...thStyle, minWidth: 100 }}>Font</th>
                            <th style={{ ...thStyle, minWidth: 90 }}>Text Size</th>
                            <th style={{ ...thStyle, width: 52 }}>Colour</th>
                            <th style={{ ...thStyle, width: 72, textAlign: 'center' }}>Underline</th>
                            <th style={{ ...thStyle, width: 52, textAlign: 'center' }}>Bold</th>
                            <th style={{ ...thStyle, minWidth: 110 }}>Arrow Head</th>
                            <th style={{ ...thStyle, width: 72 }}>Weight</th>
                            <th style={{ ...thStyle, width: 86 }}>Width Factor</th>
                            <th style={{ ...thStyle, width: 56, textAlign: 'center' }}>Border</th>
                            <th style={{ ...thStyle, width: 108 }}>Background</th>
                            <th style={{ ...thStyle, width: 32 }} />
                        </tr>
                    </thead>
                    <tbody>
                        {items.length === 0 ? (
                            <tr>
                                <td
                                    colSpan={12}
                                    style={{
                                        textAlign: 'center',
                                        padding: '24px 16px',
                                        color: 'var(--text-tertiary)',
                                        fontSize: 13,
                                    }}
                                >
                                    No types defined — click{' '}
                                    <strong>Add Type</strong> below to get started.
                                </td>
                            </tr>
                        ) : (
                            items.map((item, i) => {
                                const colourHex = item.colour
                                    ? rgbToHex(item.colour.r, item.colour.g, item.colour.b)
                                    : '#000000';
                                return (
                                    <tr key={i}>
                                        <td style={tdStyle}>
                                            <input
                                                className="input-base w-full"
                                                style={{ fontSize: 12, padding: '3px 7px', fontWeight: 600 }}
                                                placeholder="e.g. Body"
                                                value={item.name}
                                                onChange={e => patch(i, { name: e.target.value })}
                                            />
                                        </td>
                                        <td style={tdStyle}>
                                            <input
                                                className="input-base w-full"
                                                style={{ fontSize: 12, padding: '3px 7px' }}
                                                placeholder="Arial"
                                                value={item.font}
                                                onChange={e => patch(i, { font: e.target.value })}
                                            />
                                        </td>
                                        <td style={tdStyle}>
                                            <input
                                                className="input-base w-full"
                                                style={{ fontSize: 12, padding: '3px 7px' }}
                                                placeholder="2.5mm"
                                                value={item.textSize}
                                                onChange={e => patch(i, { textSize: e.target.value })}
                                            />
                                        </td>
                                        <td style={{ ...tdStyle, textAlign: 'center' }}>
                                            <input
                                                type="color"
                                                value={colourHex}
                                                onChange={e => {
                                                    const { r, g, b } = hexToRgb(e.target.value);
                                                    patch(i, { colour: { r, g, b } });
                                                }}
                                                style={{
                                                    width: 32,
                                                    height: 26,
                                                    padding: 2,
                                                    border: '1px solid var(--border-default)',
                                                    borderRadius: 4,
                                                    cursor: 'pointer',
                                                }}
                                                title={
                                                    item.colour
                                                        ? `rgb(${item.colour.r}, ${item.colour.g}, ${item.colour.b})`
                                                        : 'No colour set'
                                                }
                                            />
                                        </td>
                                        <td style={{ ...tdStyle, textAlign: 'center' }}>
                                            <input
                                                type="checkbox"
                                                checked={item.underline}
                                                onChange={e => patch(i, { underline: e.target.checked })}
                                                style={{
                                                    width: 15,
                                                    height: 15,
                                                    cursor: 'pointer',
                                                    accentColor: 'var(--accent)',
                                                }}
                                            />
                                        </td>
                                        <td style={{ ...tdStyle, textAlign: 'center' }}>
                                            <input
                                                type="checkbox"
                                                checked={item.bold}
                                                onChange={e => patch(i, { bold: e.target.checked })}
                                                style={{
                                                    width: 15,
                                                    height: 15,
                                                    cursor: 'pointer',
                                                    accentColor: 'var(--accent)',
                                                }}
                                            />
                                        </td>
                                        <td style={tdStyle}>
                                            <input
                                                className="input-base w-full"
                                                style={{ fontSize: 12, padding: '3px 7px' }}
                                                placeholder="Filled Arrow"
                                                value={item.arrowHead}
                                                onChange={e => patch(i, { arrowHead: e.target.value })}
                                            />
                                        </td>
                                        <td style={tdStyle}>
                                            <input
                                                className="input-base w-full"
                                                type="number"
                                                step={1}
                                                style={{ fontSize: 12, padding: '3px 7px' }}
                                                placeholder="1"
                                                value={item.weight}
                                                onChange={e => patch(i, { weight: e.target.value })}
                                            />
                                        </td>
                                        <td style={tdStyle}>
                                            <input
                                                className="input-base w-full"
                                                type="number"
                                                step={0.01}
                                                style={{ fontSize: 12, padding: '3px 7px' }}
                                                placeholder="1.0"
                                                value={item.widthFactor}
                                                onChange={e => patch(i, { widthFactor: e.target.value })}
                                            />
                                        </td>
                                        <td style={{ ...tdStyle, textAlign: 'center' }}>
                                            <input
                                                type="checkbox"
                                                checked={item.border}
                                                onChange={e => patch(i, { border: e.target.checked })}
                                                style={{
                                                    width: 15,
                                                    height: 15,
                                                    cursor: 'pointer',
                                                    accentColor: 'var(--accent)',
                                                }}
                                            />
                                        </td>
                                        <td style={tdStyle}>
                                            <select
                                                className="input-base w-full"
                                                style={{ fontSize: 12, padding: '3px 7px', cursor: 'pointer' }}
                                                value={item.background}
                                                onChange={e =>
                                                    patch(i, {
                                                        background: e.target.value as BackgroundValue,
                                                    })
                                                }
                                            >
                                                <option value="Opaque">Opaque</option>
                                                <option value="Transparent">Transparent</option>
                                            </select>
                                        </td>
                                        <td style={{ ...tdStyle, padding: '4px 4px 4px 0' }}>
                                            <button
                                                onClick={() =>
                                                    onChange(items.filter((_, j) => j !== i))
                                                }
                                                style={{
                                                    background: 'none',
                                                    border: 'none',
                                                    cursor: 'pointer',
                                                    color: 'var(--text-tertiary)',
                                                    padding: 4,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                }}
                                                title="Remove"
                                            >
                                                <Trash2 size={13} />
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })
                        )}
                    </tbody>
                </table>
            </div>
            <button
                className="btn-secondary text-xs flex items-center gap-1 mt-3"
                onClick={() => onChange([...items, emptyFixedType()])}
            >
                <Plus size={12} /> Add Type
            </button>
        </div>
    );
};

/* ------------------------------------------------------------------ */
/*  NomenclaturePatternEditor                                          */
/* ------------------------------------------------------------------ */

type PatternEditorProps = {
    value: string;
    onChange: (v: string) => void;
    data: TextStyleData;
};

const PatternEditor = ({ value, onChange, data }: PatternEditorProps) => {
    const inputRef = useRef<HTMLInputElement>(null);

    const insertToken = (token: string) => {
        const el = inputRef.current;
        const insert = `[${token}]`;
        if (!el) {
            onChange(value + insert);
            return;
        }
        const start = el.selectionStart ?? value.length;
        const end = el.selectionEnd ?? value.length;
        const next = value.slice(0, start) + insert + value.slice(end);
        onChange(next);
        requestAnimationFrame(() => {
            el.focus();
            const pos = start + insert.length;
            el.setSelectionRange(pos, pos);
        });
    };

    const preview = useMemo(() => {
        if (!value) return '';
        return value.replace(/\[([^\]]+)\]/g, (_, name) => {
            switch (name) {
                case 'Font':
                    return data.Font.mapped || data.Font.value || `[${name}]`;
                case 'TextSize':
                    return (
                        data.TextSizes[0]?.mapped ||
                        data.TextSizes[0]?.value ||
                        `[${name}]`
                    );
                case 'TabSize':
                    return (
                        data.TabSizes[0]?.mapped ||
                        data.TabSizes[0]?.value ||
                        `[${name}]`
                    );
                case 'Colour':
                    return data.Colours[0]?.mapped || `[${name}]`;
                case 'Underline':
                    return data.Underline.trueToken || `[${name}]`;
                case 'Bold':
                    return data.Bold.trueToken || `[${name}]`;
                case 'ArrowHead':
                    return (
                        data.ArrowHeads[0]?.mapped ||
                        data.ArrowHeads[0]?.value ||
                        `[${name}]`
                    );
                case 'Weight':
                    return (
                        data.Weights[0]?.mapped ||
                        data.Weights[0]?.value ||
                        `[${name}]`
                    );
                case 'WidthFactor':
                    return (
                        data.WidthFactors[0]?.mapped ||
                        data.WidthFactors[0]?.value ||
                        `[${name}]`
                    );
                case 'Border':
                    return data.Border.trueToken || `[${name}]`;
                case 'Background':
                    return data.Background.opaqueToken || `[${name}]`;
                default:
                    return `[${name}]`;
            }
        });
    }, [value, data]);

    return (
        <div>
            <p className="text-xs mb-2" style={{ color: 'var(--text-tertiary)' }}>
                Click a token to insert it at the cursor position, or type the pattern directly.
            </p>
            <div className="flex flex-wrap gap-1.5 mb-3">
                {NOMENCLATURE_TOKENS.map(token => (
                    <button
                        key={token}
                        onClick={() => insertToken(token)}
                        className="text-xs px-2 py-0.5 rounded-full transition-colors"
                        style={{
                            backgroundColor: 'var(--accent-soft)',
                            color: 'var(--accent-text)',
                            border: '1px solid var(--accent-border, var(--accent-soft))',
                            cursor: 'pointer',
                            fontFamily: 'monospace',
                        }}
                        title={`Insert [${token}]`}
                    >
                        [{token}]
                    </button>
                ))}
            </div>
            <input
                ref={inputRef}
                className="input-base w-full"
                style={{ fontFamily: 'monospace', fontSize: 13 }}
                placeholder="e.g. [Pattern]-[Colour]-[TextSize]"
                value={value}
                onChange={e => onChange(e.target.value)}
            />
            {preview && value !== preview && (
                <div
                    className="mt-2 px-3 py-2 rounded-lg"
                    style={{
                        backgroundColor: 'var(--bg-subtle, var(--surface-100))',
                        border: '1px solid var(--border-subtle, var(--border-default))',
                    }}
                >
                    <span className="text-xs" style={{ color: 'var(--text-tertiary)' }}>
                        Preview (first values):{' '}
                    </span>
                    <span
                        className="text-xs font-semibold"
                        style={{ color: 'var(--text-primary)', fontFamily: 'monospace' }}
                    >
                        {preview}
                    </span>
                </div>
            )}
        </div>
    );
};

/* ------------------------------------------------------------------ */
/*  Main component                                                     */
/* ------------------------------------------------------------------ */

type TextStyleStandardProps = {
    projectId: string;
    style?: CSSProperties;
    onBack: () => void;
};

const TextStyleStandard = ({ projectId, style, onBack }: TextStyleStandardProps) => {
    const [data, setData] = useState<TextStyleData>(defaultData());
    const [isDirty, setIsDirty] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [isLoadingBlob, setIsLoadingBlob] = useState(false);
    const [saveStatus, setSaveStatus] = useState<'idle' | 'saved' | 'error'>('idle');

    const download = useDownloadAttachment();
    const httpClient = useLoadHttpClientEmpty();

    const { data: queryData, loading: queryLoading, refetch } = useQuery(
        GET_PROJECT_TEXT_STYLE_DATA,
        { variables: { id: projectId }, skip: !projectId, fetchPolicy: 'network-only' },
    );

    const [setDynamicDataMutation] = useMutation(SET_PROJECT_DYNAMIC_DATA);

    const existingEntry = useMemo(() => {
        const entries: any[] = queryData?.Project?.dynamicData ?? [];
        return entries.find(
            d => d?.type === DYNAMIC_DATA_TYPE && d?.name === DYNAMIC_DATA_NAME,
        ) as { id: string; name: string; type: string; data?: string; setData?: string } | undefined;
    }, [queryData]);

    /* ── Load blob ──────────────────────────────────────────────── */
    useEffect(() => {
        if (queryLoading || !existingEntry?.data) return;
        setIsLoadingBlob(true);
        download(existingEntry.data)
            .then(raw => {
                const str = typeof raw === 'string' ? raw : JSON.stringify(raw);
                const parsed = JSON.parse(str) as Partial<TextStyleData> & {
                    TabSize?: { value: number | string; mapped?: string } | number;
                };
                // Merge with defaults so blobs saved before new fields were added
                // don't leave list/bool properties undefined.
                const { TabSize: legacyTabSize, ...parsedData } = parsed;
                const tabSizes = Array.isArray(parsed.TabSizes)
                    ? parsed.TabSizes
                    : legacyTabSize == null
                        ? defaultData().TabSizes
                        : [{
                            value: String(
                                typeof legacyTabSize === 'number'
                                    ? legacyTabSize
                                    : legacyTabSize.value,
                            ),
                            mapped:
                                typeof legacyTabSize === 'number'
                                    ? ''
                                    : legacyTabSize.mapped ?? '',
                        }];
                setData({
                    ...defaultData(),
                    ...parsedData,
                    TabSizes: tabSizes,
                });
            })
            .catch(() => {})
            .finally(() => setIsLoadingBlob(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [existingEntry?.data, queryLoading]);

    /* ── Save ───────────────────────────────────────────────────── */
    const handleSave = useCallback(async () => {
        setIsSaving(true);
        setSaveStatus('idle');
        try {
            if (!existingEntry) {
                await setDynamicDataMutation({
                    variables: {
                        projectId,
                        data: {
                            name: DYNAMIC_DATA_NAME,
                            type: DYNAMIC_DATA_TYPE,
                            description:
                                'Text style standard defining allowed values and nomenclature pattern',
                        },
                    },
                });
            }
            const refetched = await refetch();
            const entry: any = (refetched.data?.Project?.dynamicData ?? []).find(
                (d: any) => d?.type === DYNAMIC_DATA_TYPE && d?.name === DYNAMIC_DATA_NAME,
            );
            if (!entry?.setData) throw new Error('No upload URL returned');
            await httpClient.put(entry.setData, JSON.stringify(data), {
                headers: { 'x-ms-blob-type': 'BlockBlob', 'Content-Type': 'application/json', 'Cache-Control': 'no-cache, no-store, must-revalidate' },
            });
            setIsDirty(false);
            setSaveStatus('saved');
            setTimeout(() => setSaveStatus('idle'), 3000);
        } catch {
            setSaveStatus('error');
            setTimeout(() => setSaveStatus('idle'), 4000);
        } finally {
            setIsSaving(false);
        }
    }, [data, existingEntry, projectId, setDynamicDataMutation, httpClient, refetch]);

    /* ── Navigation guard ───────────────────────────────────────── */
    const handleBack = useCallback(() => {
        if (isDirty && !window.confirm('You have unsaved changes. Leave without saving?')) return;
        onBack();
    }, [isDirty, onBack]);

    /* ── Typed state updater ────────────────────────────────────── */
    const update = useCallback((fn: (prev: TextStyleData) => TextStyleData) => {
        setData(fn);
        setIsDirty(true);
    }, []);

    /* ── Loading state ──────────────────────────────────────────── */
    if (queryLoading || isLoadingBlob) {
        return (
            <div className="flex items-center justify-center h-full" style={style}>
                <Loader2
                    size={20}
                    className="animate-spin"
                    style={{ color: 'var(--text-tertiary)' }}
                />
            </div>
        );
    }

    return (
        <div className="flex flex-col h-full" style={style}>
            {/* ── Header ─────────────────────────────────────────── */}
            <div
                className="flex items-center gap-3 px-4 py-2.5 border-b flex-shrink-0"
                style={{ borderColor: 'var(--border-default)' }}
            >
                <button
                    className="flex items-center gap-1 text-sm font-medium px-2 py-1 rounded-md transition-colors"
                    style={{
                        color: 'var(--accent-text)',
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                    }}
                    onClick={handleBack}
                >
                    <ArrowLeft size={15} /> Back
                </button>
                <span
                    className="flex-1 text-sm font-semibold"
                    style={{ color: 'var(--text-primary)' }}
                >
                    Text Style Standard
                    {isDirty && (
                        <span
                            className="ml-2 text-xs font-normal"
                            style={{ color: 'var(--text-tertiary)' }}
                        >
                            · Unsaved changes
                        </span>
                    )}
                </span>
                <div className="flex items-center gap-2">
                    {saveStatus === 'saved' && (
                        <span
                            className="flex items-center gap-1 text-xs"
                            style={{ color: 'var(--success, #16a34a)' }}
                        >
                            <Check size={12} /> Saved
                        </span>
                    )}
                    {saveStatus === 'error' && (
                        <span
                            className="flex items-center gap-1 text-xs"
                            style={{ color: 'var(--error, #dc2626)' }}
                        >
                            <AlertCircle size={12} /> Save failed
                        </span>
                    )}
                    <button
                        className="btn-primary flex items-center gap-1.5 text-sm"
                        onClick={handleSave}
                        disabled={isSaving || !isDirty}
                        style={{ opacity: !isDirty ? 0.5 : 1 }}
                    >
                        {isSaving ? (
                            <Loader2 size={13} className="animate-spin" />
                        ) : (
                            <Save size={13} />
                        )}
                        Save
                    </button>
                </div>
            </div>

            {/* ── Body ───────────────────────────────────────────── */}
            <div className="flex-1 overflow-auto px-6 py-5">

                {/* Font */}
                <SectionCard
                    title="Font"
                    description="The typeface applied to the text style. Enter the exact Revit font name and its nomenclature token."
                >
                    <div className="flex items-end gap-3">
                        <div className="flex-1">
                            <label
                                className="text-xs mb-1 block"
                                style={{ color: 'var(--text-tertiary)' }}
                            >
                                        Font Name
                                    </label>
                                    <input
                                        className="input-base w-full"
                                        style={{ fontSize: 13 }}
                                        placeholder="e.g. Arial"
                                        value={data.Font.value}
                                        onChange={e =>
                                            update(p => ({
                                                ...p,
                                                Font: { ...p.Font, value: e.target.value },
                                            }))
                                        }
                                    />
                                </div>
                                <div style={{ width: 160 }}>
                                    <label
                                        className="text-xs mb-1 block"
                                        style={{ color: 'var(--text-tertiary)' }}
                                    >
                                        Mapped Token
                                    </label>
                                    <input
                                        className="input-base w-full"
                                        style={{ fontSize: 13, fontFamily: 'monospace' }}
                                        placeholder="token"
                                        value={data.Font.mapped}
                                        onChange={e =>
                                            update(p => ({
                                                ...p,
                                                Font: { ...p.Font, mapped: e.target.value },
                                            }))
                                        }
                                    />
                                </div>
                            </div>
                        </SectionCard>

                        {/* Text Sizes */}
                        <SectionCard
                            title="Text Sizes"
                            description="Allowed text size values (e.g. 2.5mm, 3.5mm). Each value has a mapped token used in the nomenclature pattern."
                        >
                            <MappedListEditor
                                items={data.TextSizes}
                                onChange={items => update(p => ({ ...p, TextSizes: items }))}
                                placeholder="e.g. 2.5mm"
                                addLabel="Add Size"
                            />
                        </SectionCard>

                        {/* Tab Sizes */}
                        <SectionCard
                            title="Tab Sizes"
                            description="Allowed tab size values (decimals, e.g. 1.0, 1.5). Each value has a mapped token used in the nomenclature pattern."
                        >
                            <MappedListEditor
                                items={data.TabSizes}
                                onChange={items => update(p => ({ ...p, TabSizes: items }))}
                                placeholder="e.g. 1.0"
                                addLabel="Add Tab Size"
                                valueType="number"
                                step={0.1}
                            />
                        </SectionCard>

                        {/* Colours */}
                        <SectionCard
                            title="Colours"
                            description="Allowed text style colours. Use the colour picker to choose each colour; assign a mapped token for nomenclature."
                        >
                            <ColourListEditor
                                items={data.Colours}
                                onChange={items => update(p => ({ ...p, Colours: items }))}
                            />
                            <div
                                className="mt-4 pt-4"
                                style={{ borderTop: '1px solid var(--border-subtle, var(--border-default))' }}
                            >
                                <label
                                    className="text-xs mb-1 block"
                                    style={{ color: 'var(--text-tertiary)' }}
                                >
                                    Colour Tolerance
                                </label>
                                <p className="text-xs mb-3" style={{ color: 'var(--text-tertiary)' }}>
                                    How far an actual colour can differ from an allowed colour and still
                                    be considered a match.
                                </p>
                                <div className="flex flex-col gap-2">
                                    {COLOUR_TOLERANCE_OPTIONS.map(opt => {
                                        const selected = data.ColourTolerance === opt.value;
                                        return (
                                            <button
                                                key={opt.value}
                                                type="button"
                                                onClick={() =>
                                                    update(p => ({ ...p, ColourTolerance: opt.value }))
                                                }
                                                className="text-left rounded-lg px-3 py-2 transition-colors"
                                                style={{
                                                    border: `1px solid ${selected ? 'var(--accent)' : 'var(--border-default)'}`,
                                                    backgroundColor: selected
                                                        ? 'var(--accent-soft)'
                                                        : 'transparent',
                                                    cursor: 'pointer',
                                                }}
                                            >
                                                <div className="flex items-center justify-between gap-2">
                                                    <span
                                                        className="text-sm font-medium"
                                                        style={{
                                                            color: selected
                                                                ? 'var(--accent-text)'
                                                                : 'var(--text-primary)',
                                                        }}
                                                    >
                                                        {opt.label}
                                                    </span>
                                                    <span
                                                        className="text-xs font-semibold"
                                                        style={{
                                                            color: selected
                                                                ? 'var(--accent-text)'
                                                                : 'var(--text-tertiary)',
                                                            fontFamily: 'monospace',
                                                        }}
                                                    >
                                                        {opt.value.toFixed(1)}
                                                    </span>
                                                </div>
                                                <p
                                                    className="text-xs mt-0.5"
                                                    style={{ color: 'var(--text-tertiary)' }}
                                                >
                                                    {opt.description}
                                                </p>
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        </SectionCard>

                        {/* Boolean properties */}
                        <SectionCard
                            title="Style Options"
                            description="Map style states to nomenclature tokens. Leave a token blank to omit that state from the generated name."
                        >
                            <div className="flex flex-col gap-4">
                                <BoolMappedEditor
                                    label="Underline"
                                    value={data.Underline}
                                    onChange={v => update(p => ({ ...p, Underline: v }))}
                                />
                                <BoolMappedEditor
                                    label="Bold"
                                    value={data.Bold}
                                    onChange={v => update(p => ({ ...p, Bold: v }))}
                                />
                                <BoolMappedEditor
                                    label="Border"
                                    value={data.Border}
                                    onChange={v => update(p => ({ ...p, Border: v }))}
                                />
                                <BackgroundMappedEditor
                                    label="Background"
                                    value={data.Background}
                                    onChange={v => update(p => ({ ...p, Background: v }))}
                                />
                            </div>
                        </SectionCard>

                        {/* Weights */}
                        <SectionCard
                            title="Weights"
                            description="Allowed pen weight values (whole numbers). Each value has a mapped token used in the nomenclature pattern."
                        >
                            <MappedListEditor
                                items={data.Weights}
                                onChange={items => update(p => ({ ...p, Weights: items }))}
                                placeholder="e.g. 1"
                                addLabel="Add Weight"
                            />
                        </SectionCard>

                        {/* Width Factors */}
                        <SectionCard
                            title="Width Factors"
                            description="Allowed width factor values (decimals, e.g. 1.0, 0.8). Each value has a mapped token used in the nomenclature pattern."
                        >
                            <MappedListEditor
                                items={data.WidthFactors}
                                onChange={items => update(p => ({ ...p, WidthFactors: items }))}
                                placeholder="e.g. 1.0"
                                addLabel="Add Width Factor"
                            />
                        </SectionCard>

                        {/* Arrow Heads */}
                        <SectionCard
                            title="Arrow Heads"
                            description="Allowed arrowhead types for line terminators. Each value has a mapped token."
                        >
                            <MappedListEditor
                                items={data.ArrowHeads}
                                onChange={items => update(p => ({ ...p, ArrowHeads: items }))}
                                placeholder="e.g. Filled Arrow"
                                addLabel="Add Arrow Head"
                            />
                        </SectionCard>

                        {/* Nomenclature Pattern */}
                        <SectionCard
                            title="Nomenclature Pattern"
                            description="Define how a line style name is composed from the property tokens above."
                        >
                            <PatternEditor
                                value={data.NomenclaturePattern}
                                onChange={v => update(p => ({ ...p, NomenclaturePattern: v }))}
                                data={data}
                            />
                        </SectionCard>

                {/* Fixed Types */}
                <SectionCard
                    title="Fixed Types"
                    description="Define each named text style type and set its exact property values. No new types beyond this list will be permitted."
                >
                    <FixedTypesEditor
                        items={data.FixedTypes}
                        onChange={items => update(p => ({ ...p, FixedTypes: items }))}
                    />
                </SectionCard>

            </div>
        </div>
    );
};

export default TextStyleStandard;
